// Pure doc-derived geometry shared by the engine renderers AND the io/ exporters (ARC-3a):
// dimension geometry/formatting (ANN-3/4/5), grid labels (ANN-6), entity bounds, doc extents.
import {
  angleOf,
  dir,
  dist,
  emptyBounds,
  extendBounds,
  normalizeDeg,
  rotate,
  sub,
  type Bounds,
  type Vec
} from "../core/geometry";
import { arcContainsAngle, arcPoint } from "../core/intersect";
import { formatLength } from "../core/units";
import { dimensionEndpoints } from "./anchors";
import type { DimensionEntity, Doc, Entity, LabelEntity, TextStyle } from "./entities";
import { imageCorners } from "./imageGeom";

// ---- dimensions (ANN-3/4/5) ---------------------------------------------------------

export interface DimGeometry {
  a: Vec;
  b: Vec;
  pa: Vec; // dimension-line endpoints
  pb: Vec;
  value: number;
  textPos: Vec;
  textAngleDeg: number;
}

/** Full dimension geometry from CURRENT anchors, or null when they are broken. */
export function dimensionGeometry(doc: Doc, e: DimensionEntity): DimGeometry | null {
  const ends = dimensionEndpoints(doc, e);
  if (!ends) return null;
  const { a, b } = ends;

  if (e.kind === "radius" || e.kind === "diameter") {
    const ref = e.a.kind === "entity" ? doc.entities[e.a.entityId] : undefined;
    const r = ref && (ref.type === "circle" || ref.type === "arc") ? ref.r : dist(a, b);
    const toward = dist(b, a) > 1e-9 ? dir(angleOf(sub(b, a))) : { x: 1, y: 0 };
    const edge = { x: a.x + toward.x * r, y: a.y + toward.y * r };
    return {
      a,
      b,
      pa: edge,
      pb: b,
      value: e.kind === "diameter" ? r * 2 : r,
      textPos: b,
      textAngleDeg: 0
    };
  }

  let pa: Vec;
  let pb: Vec;
  let value: number;
  if (e.orient === "h") {
    const yL = (a.y + b.y) / 2 + e.offset;
    pa = { x: a.x, y: yL };
    pb = { x: b.x, y: yL };
    value = Math.abs(b.x - a.x);
  } else if (e.orient === "v") {
    const xL = (a.x + b.x) / 2 + e.offset;
    pa = { x: xL, y: a.y };
    pb = { x: xL, y: b.y };
    value = Math.abs(b.y - a.y);
  } else {
    const d = dist(a, b);
    const u = d > 1e-9 ? dir(angleOf(sub(b, a))) : { x: 1, y: 0 };
    const n = { x: -u.y, y: u.x };
    pa = { x: a.x + n.x * e.offset, y: a.y + n.y * e.offset };
    pb = { x: b.x + n.x * e.offset, y: b.y + n.y * e.offset };
    value = d;
  }
  const mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 };
  let deg = normalizeDeg(angleOf(sub(pb, pa)));
  if (deg > 90 && deg <= 270) deg = normalizeDeg(deg + 180); // never upside down (ANN-3)
  return { a, b, pa, pb, value, textPos: mid, textAngleDeg: e.orient === "h" ? 0 : deg };
}

export function dimensionText(doc: Doc, e: DimensionEntity, value: number): string {
  if (e.textOverride) return `${e.textOverride} ✎`; // EC-ANN-3 override marker
  const precision = e.precisionOverride ?? doc.precision;
  const auto = e.kind === "radius" ? "R" : e.kind === "diameter" ? "⌀" : "";
  return `${e.prefix ?? ""}${auto}${formatLength(value, doc.units, precision)}${e.suffix ?? ""}`;
}

/**
 * The box of a label's TEXT, at its tail. This is the part a person clicks to select
 * the label: the leader line and the head are not (owner, 2026-10-20: the selection
 * covered critical parts). The head moves by its own grip.
 */
export function labelTextBox(e: LabelEntity): Bounds {
  const size = e.textStyle.size;
  // the real width in a browser (the box fits the text); outside one (tests, export workers) an estimate
  const textW = (measureTextWidth(e.content, e.textStyle) ?? size * Math.max(e.content.length, 1) * 0.6) + 1;
  const right = e.tail.x >= e.head.x;
  const x0 = right ? e.tail.x : e.tail.x - textW;
  return { minX: x0, minY: e.tail.y - size * 0.65, maxX: x0 + textW, maxY: e.tail.y + size * 0.65 };
}

// The same font stacks as the renderer (engine/renderAnnotation.ts FONT_STACKS); doc/ may not import engine/.
const MEASURE_FONTS: Record<TextStyle["font"], string> = {
  Inter: "Inter, system-ui, sans-serif",
  Caveat: "Caveat, cursive",
  "JetBrains Mono": "'JetBrains Mono', Consolas, monospace"
};
let measureCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null | undefined;

/** The drawn width of one line of text in world units, or null where no canvas exists. */
function measureTextWidth(text: string, ts: TextStyle): number | null {
  if (measureCtx === undefined) {
    try {
      measureCtx =
        typeof OffscreenCanvas !== "undefined"
          ? new OffscreenCanvas(1, 1).getContext("2d")
          : typeof document !== "undefined"
            ? document.createElement("canvas").getContext("2d")
            : null;
    } catch {
      measureCtx = null;
    }
  }
  if (!measureCtx) return null;
  measureCtx.font = `${ts.bold ? 700 : 400} ${ts.size}px ${MEASURE_FONTS[ts.font]}`;
  const w = measureCtx.measureText(text).width;
  return Number.isFinite(w) && w > 0 ? w : null;
}

// ---- grid labels (ANN-6) --------------------------------------------------------------

/** A1-style labels: numbers from 1, letters A..Z, AA.. (EC-ANN-7 continues AAA). */
export function indexLabel(i: number, mode: "letters" | "numbers"): string {
  if (mode === "numbers") return String(i + 1);
  let n = i;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

// ---- bounds (RND-5) ---------------------------------------------------------------------

function pad(b: Bounds, p: number): Bounds {
  return { minX: b.minX - p, minY: b.minY - p, maxX: b.maxX + p, maxY: b.maxY + p };
}

function strokePad(entity: Entity): number {
  const style = "style" in entity ? entity.style : null;
  return style && style.widthMode === "world" ? style.strokeWidth / 2 : 0;
}

function extendRotatedRect(b: Bounds, origin: Vec, w: number, h: number, deg: number): void {
  const corners: Vec[] = [
    origin,
    { x: origin.x + w, y: origin.y },
    { x: origin.x + w, y: origin.y + h },
    { x: origin.x, y: origin.y + h }
  ];
  for (const c of corners) extendBounds(b, Math.abs(deg) > 1e-9 ? rotate(c, origin, deg) : c);
}

/** AABB for one entity; groups return the union of children (doc needed for lookup). */
export function entityBounds(entity: Entity, doc: Doc): Bounds {
  const b = emptyBounds();
  switch (entity.type) {
    case "polyline":
    case "marker":
      for (const p of entity.points) extendBounds(b, p);
      break;
    case "circle":
      extendBounds(b, { x: entity.center.x - entity.r, y: entity.center.y - entity.r });
      extendBounds(b, { x: entity.center.x + entity.r, y: entity.center.y + entity.r });
      break;
    case "arc": {
      extendBounds(b, arcPoint(entity.center, entity.r, entity.startDeg));
      extendBounds(b, arcPoint(entity.center, entity.r, entity.startDeg + entity.sweepDeg));
      for (const q of [0, 90, 180, 270]) {
        if (arcContainsAngle(entity.startDeg, entity.sweepDeg, normalizeDeg(q))) {
          extendBounds(b, arcPoint(entity.center, entity.r, q));
        }
      }
      break;
    }
    case "text": {
      const lines = entity.content.split("\n");
      const maxLen = Math.max(1, ...lines.map((l) => l.length));
      const w = entity.textStyle.size * maxLen * 0.6;
      const h = entity.textStyle.size * 1.3 * lines.length;
      const x0 =
        entity.textStyle.align === "center"
          ? entity.pos.x - w / 2
          : entity.textStyle.align === "right"
            ? entity.pos.x - w
            : entity.pos.x;
      extendRotatedRect(b, { x: x0, y: entity.pos.y }, w, h, entity.rotationDeg);
      extendBounds(b, entity.pos);
      break;
    }
    case "label": {
      extendBounds(b, entity.head);
      extendBounds(b, entity.tail);
      const textW = entity.textStyle.size * Math.max(entity.content.length, 1) * 0.6 + 1;
      const right = entity.tail.x >= entity.head.x;
      extendBounds(b, {
        x: entity.tail.x + (right ? textW : -textW),
        y: entity.tail.y + entity.textStyle.size
      });
      extendBounds(b, { x: entity.tail.x, y: entity.tail.y - entity.textStyle.size });
      break;
    }
    case "image":
      for (const p of imageCorners(entity)) extendBounds(b, p); // rotation-aware
      break;
    case "grid": {
      const margin = Math.max(entity.cellW, entity.cellH) * 0.5; // label margin (ANN-6)
      extendRotatedRect(
        b,
        { x: entity.pos.x - margin, y: entity.pos.y - margin },
        entity.cols * entity.cellW + margin,
        entity.rows * entity.cellH + margin,
        entity.rotationDeg
      );
      break;
    }
    case "dimension": {
      const g = dimensionGeometry(doc, entity);
      if (g) {
        const p = 5; // arrows + text
        for (const q of [g.a, g.b, g.pa, g.pb, g.textPos]) {
          extendBounds(b, { x: q.x - p, y: q.y - p });
          extendBounds(b, { x: q.x + p, y: q.y + p });
        }
      }
      break;
    }
    case "group":
      for (const childId of entity.children) {
        const child = doc.entities[childId];
        if (!child) continue;
        const cb = entityBounds(child, doc);
        extendBounds(b, { x: cb.minX, y: cb.minY });
        extendBounds(b, { x: cb.maxX, y: cb.maxY });
      }
      break;
  }
  return pad(b, strokePad(entity));
}

/** Bounds of all visible entities (zoom extents, export extents). */
export function docBounds(doc: Doc, includedLayerIds?: ReadonlySet<string>): Bounds {
  const b = emptyBounds();
  const layerVisible = new Map(doc.layers.map((l) => [l.id, l.visible]));
  for (const entity of Object.values(doc.entities)) {
    if (entity.hidden || entity.type === "group") continue;
    if (includedLayerIds ? !includedLayerIds.has(entity.layerId) : !layerVisible.get(entity.layerId)) continue;
    const eb = entityBounds(entity, doc);
    if (eb.minX <= eb.maxX) {
      extendBounds(b, { x: eb.minX, y: eb.minY });
      extendBounds(b, { x: eb.maxX, y: eb.maxY });
    }
  }
  return b;
}
